import {acknowledgeReportExports, fetchMyReportExports, type ReportExport} from '@/modules/reports/api/reports-export'
import {toaster} from '@cypro/ui/toaster'
import {useQuery} from '@tanstack/react-query'
import {useTranslate} from '@tolgee/react'
import {useEffect, useRef} from 'react'

export const reportExportsQueryKey = ['report-exports']

/** Only a running export is worth polling for. */
function hasPendingExport(exports: ReportExport[] | undefined) {
  return Boolean(exports?.some(item => item.status === 'queued' || item.status === 'running'))
}

/**
 * Announces finished background exports wherever the user is, not only while the export dialog is
 * open. The announcement is recorded on the row, so an export is toasted exactly once — also across
 * reloads, and never again when the dialog is opened later.
 */
export function useReportExportNotifications(enabled: boolean) {
  const {t} = useTranslate('reports')

  const query = useQuery({
    queryKey: reportExportsQueryKey,
    queryFn: fetchMyReportExports,
    enabled,
    refetchInterval: query => (hasPendingExport(query.state.data) ? 5000 : false),
  })

  // ids already announced in this session, so a failed acknowledge cannot repeat the toast
  const announcedRef = useRef(new Set<string>())
  const exports = query.data

  useEffect(() => {
    if (!exports?.length) return

    const fresh = exports.filter(
      item =>
        (item.status === 'ready' || item.status === 'failed') &&
        item.acknowledgedAt === null &&
        !announcedRef.current.has(item.id)
    )
    if (fresh.length === 0) return

    for (const item of fresh) {
      announcedRef.current.add(item.id)
      if (item.status === 'ready') toaster.success({title: t('export_ready')})
      else toaster.error({title: t('export_failed')})
    }

    void acknowledgeReportExports(fresh.map(item => item.id)).catch(() => undefined)
  }, [exports, t])
}
